import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';

export interface BusinessPayoutMethod {
  id: number;
  label: string;
  method_type: 'bank_transfer' | 'cash';
  institution_name: string;
  bank_id?: number | null;
  account_holder: string;
  account_last4: string;
  account_number: string | null;
  bank_account_type?: 'checking' | 'savings';
  /** Histórico: ya no se recolecta en el formulario, pero puede seguir presente en métodos antiguos. */
  payout_frequency?: 'weekly' | 'biweekly' | 'monthly' | 'custom';
  is_primary: boolean;
}

export interface PayoutMethodPayload {
  label: string;
  method_type: 'bank_transfer';
  institution_name: string;
  bank_id?: number | null;
  account_holder: string;
  account_number?: string;
  bank_account_type?: 'checking' | 'savings';
  is_primary: boolean;
}

export interface PayoutOverview {
  business_id: number;
  balance: {
    available: number;
    unallocated: number;
    in_settlement: number;
    paid_total: number;
    payments: number;
  };
  /** Desglose por origen de ingreso y quién le debe a quién (plataforma vs. externo). */
  origin_breakdown?: {
    total_gross: number;
    platform_gross: number;
    external_gross: number;
    commission_rate: number;
    commission_amount: number;
    company_owes_business: number;
    business_owes_company: number;
  };
  methods: BusinessPayoutMethod[];
  transactions: any[];
  total_gross_generated?: number;
  settlements: any[];
  settlements_total?: number;
  settlements_page?: number;
  settlements_limit?: number;
  reservation_payments: any[];
  reservation_payments_total?: number;
  reservation_payments_page?: number;
  reservation_payments_limit?: number;
  /** Historial de liquidaciones de DEUDA (negocio -> TuSala), compartido tal cual entre el propietario y el super admin. */
  debt_settlements?: BusinessDebtSettlement[];
}

// Fila del "Pagos de reservas" del modal de super admin -- una tarjeta por
// GRUPO (conjunto multi-día colapsado en una sola fila, o una reserva
// suelta), con sus fechas individuales ya incluidas en `members` para que
// el panel pueda desplegarlas sin otra llamada al backend. Ver el
// comentario junto a getAdminBusinessReservationPayments en el backend.
export interface AdminReservationPaymentMember {
  payment_id: number;
  reservation_id: number;
  amount: number;
  status: string;
  payment_date: string;
  payment_provider?: string | null;
  transaction_id?: string | null;
  payment_origin?: string | null;
  business_reservation_number: number;
  reservation_group_id?: string | null;
  reservation_date?: string;
  space_title: string;
  settlement_id?: number | null;
  debt_settlement_id?: number | null;
  gross_amount: number;
  commission_amount: number;
  net_amount: number;
  commission_rate: number;
}

export interface AdminReservationPaymentGroup {
  group_key: string;
  is_group: boolean;
  payment_count: number;
  space_title: string;
  min_reservation_number: number;
  max_reservation_number: number;
  representative_date: string;
  gross_amount: number;
  commission_amount: number;
  net_amount: number;
  commission_rate: number;
  // true cuando NINGÚN pago del grupo pasó por la pasarela (todo cobrado
  // directo por el negocio) -- en ese caso "Procesado"/"Pendiente de
  // transferencia" no aplica, ver getAdminBusinessReservationPayments.
  all_external: boolean;
  all_settled: boolean;
  members: AdminReservationPaymentMember[];
}

export interface BusinessDebtSettlement {
  id: number;
  business_id: number;
  period_start: string;
  period_end: string;
  total_gross_amount: number;
  platform_gross_amount: number;
  external_gross_amount: number;
  commission_rate: number;
  commission_amount: number;
  amount_due: number;
  paid_amount: number;
  outstanding_amount: number;
  status: 'pending' | 'paid' | 'cancelled';
  payment_method: string | null;
  payment_reference: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
}

@Injectable({ providedIn: 'root' })
export class PayoutService {
  private readonly url = `${environment.apiUrl}/payouts`;

  constructor(private readonly http: HttpClient) {}

  business(id: number, page = 1, limit = 25, settlementsPage = 1, settlementsLimit = 25) {
    return this.http.get<PayoutOverview>(`${this.url}/business/${id}`, {
      params: { page, limit, settlements_page: settlementsPage, settlements_limit: settlementsLimit },
    });
  }

  saveMethod(id: number, data: PayoutMethodPayload) {
    return this.http.post<{ method: BusinessPayoutMethod }>(`${this.url}/business/${id}/methods`, data);
  }

  updateMethod(id: number, methodId: number, data: PayoutMethodPayload) {
    return this.http.patch<{ method: BusinessPayoutMethod }>(`${this.url}/business/${id}/methods/${methodId}`, data);
  }

  primary(id: number, methodId: number) {
    return this.http.patch<{ method: BusinessPayoutMethod }>(`${this.url}/business/${id}/methods/${methodId}/primary`, {});
  }

  archive(id: number, methodId: number) {
    return this.http.delete<{ msg: string }>(`${this.url}/business/${id}/methods/${methodId}`);
  }

  // Enlace de pago de una liquidación de deuda propia (self-service, no
  // requiere esperar a que un super admin lo comparta).
  getOwnDebtSettlementPaymentLink(businessId: number, settlementId: number) {
    return this.http.get<{ paymentLinkUrl: string }>(`${this.url}/business/${businessId}/debt-settlements/${settlementId}/payment-link`);
  }

  // Cierra y cobra la deuda VIGENTE (periodo automático: desde el pago más
  // antiguo sin liquidar hasta hoy) de inmediato -- sin esperar a que termine
  // el mes ni a que un super admin lo haga con los calendarios.
  createOwnDebtSettlement(businessId: number) {
    return this.http.post<{ settlement: BusinessDebtSettlement; paymentLinkUrl?: string }>(`${this.url}/business/${businessId}/debt-settlements/current`, {});
  }

  adminBusinesses(search = '', page = 1, limit = 25) {
    const params: Record<string, string | number> = { page, limit };
    if (search) params['search'] = search;
    return this.http.get<any>(`${this.url}/admin/businesses`, { params });
  }

  adminDetail(id: number, page = 1, limit = 25, settlementsPage = 1, settlementsLimit = 25) {
    return this.http.get<any>(`${this.url}/admin/businesses/${id}`, {
      params: { page, limit, settlements_page: settlementsPage, settlements_limit: settlementsLimit },
    });
  }

  // "Pagos de reservas" del modal, agrupada por conjunto multi-día --
  // endpoint propio, separado de adminDetail() (ver el comentario junto a
  // getAdminBusinessReservationPayments en el backend).
  adminBusinessReservationPayments(businessId: number, page = 1, limit = 25, search = '', month = 0, year = 0) {
    const params: Record<string, string | number> = { page, limit };
    if (search) params['search'] = search;
    if (month) params['month'] = month;
    if (year) params['year'] = year;
    return this.http.get<{
      reservation_payments: AdminReservationPaymentGroup[];
      reservation_payments_total: number;
      reservation_payments_page: number;
      reservation_payments_limit: number;
      reservation_payments_pages: number;
    }>(`${this.url}/admin/businesses/${businessId}/reservation-payments`, { params });
  }

  adminReservations(page = 1, limit = 25, search = '') {
    return this.http.get<any>(`${this.url}/admin/reservations`, { params: search ? { page, limit, search } : { page, limit } });
  }

  createSettlement(id: number, data: any) {
    return this.http.post<any>(`${this.url}/admin/businesses/${id}/settlements`, data);
  }

  transfer(id: number, data: any) {
    return this.http.post<any>(`${this.url}/admin/settlements/${id}/transfers`, data);
  }

  recordBusinessTransfer(id: number, data: { amount: number; payout_method_id: number; transfer_reference?: string; notes?: string }) {
    return this.http.post<any>(`${this.url}/admin/businesses/${id}/transfers`, data);
  }

  // Deuda del negocio hacia TuSala (dirección contraria a los métodos de
  // arriba) -- ver comentario en payouts.controller.ts.
  createDebtSettlement(businessId: number, data: { period_start?: string; period_end?: string; notes?: string }) {
    return this.http.post<{ settlement: BusinessDebtSettlement }>(`${this.url}/admin/businesses/${businessId}/debt-settlements`, data);
  }

  confirmDebtSettlementPayment(settlementId: number, data: { payment_method: string; payment_reference?: string; notes?: string }) {
    return this.http.post<{ settlement: BusinessDebtSettlement }>(`${this.url}/admin/debt-settlements/${settlementId}/confirm-payment`, data);
  }

  getDebtSettlementPaymentLink(settlementId: number) {
    return this.http.get<{ paymentLinkUrl: string }>(`${this.url}/admin/debt-settlements/${settlementId}/payment-link`);
  }

  sendDebtSettlementPaymentLinkEmail(settlementId: number) {
    return this.http.post<{ msg: string }>(`${this.url}/admin/debt-settlements/${settlementId}/send-payment-link-email`, {});
  }
}
